import React, { useState } from "react";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import ErrorMessage from "../Elements/ErrorMessage/ErrorMessage";
import SuccessMessage from "../Elements/SuccessMessage/SuccessMessage";
import {
  createNotificationForUser,
  createNotificationForRole,
  createNotificationForAll,
} from "../../services/NotificationService";
import "./AdminModalForm.css";

// Admin modal for creating notifications with different target types
const AdminCreateNotificationModal = ({ isOpen, onClose, token, onCreated }) => {
  // Form state management
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "ok",
    targetType: "all",
    targetId: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Handle all form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  // Prepare payload based on target type
  const getPayload = () => ({
    name: formData.name.trim(),
    description: formData.description.trim(),
    type: formData.type,
  });

  // Execute API call based on target type
  const executeNotificationCreation = async (payload) => {
    switch (formData.targetType) {
      case "all":
        return createNotificationForAll(token, payload);
      case "user":
        return createNotificationForUser(token, {
          ...payload,
          user_id: formData.targetId,
        });
      case "role":
        return createNotificationForRole(token, {
          ...payload,
          role_id: Number(formData.targetId),
        });
      default:
        throw new Error("Invalid target type");
    }
  };

  // Main form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      setSaving(true);
      const payload = getPayload();
      await executeNotificationCreation(payload);

      setSuccess("Notification created successfully!");
      setTimeout(() => {
        onCreated?.();
        onClose();
      }, 1500);
    } catch (err) {
      setError(err.message || "Failed to create notification");
    } finally {
      setSaving(false);
    }
  };

  // Don't render if modal is closed
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title="Create Notification"
      actions={
        <>
          <SecondButton onClick={onClose} disabled={saving}>
            Cancel
          </SecondButton>
          <MainButton onClick={handleSubmit} disabled={saving}>
            {saving ? "Creating..." : "Create Notification"}
          </MainButton>
        </>
      }
    >
      {/* Notification form */}
      <form onSubmit={handleSubmit} className="admin-modal-form">
        {/* Notification name field */}
        <label>
          Name *
          <MainInput
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            disabled={saving}
            placeholder="Notification title"
          />
        </label>

        {/* Notification description field */}
        <label>
          Description *
          <MainInput
            type="textarea"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            disabled={saving}
            rows="2"
            placeholder="Notification message"
          />
        </label>

        {/* Notification type selector */}
        <label>
          Type *
          <div className="admin-modal-select-wrapper">
            <select
              name="type"
              value={formData.type}
              onChange={handleChange}
              disabled={saving}
              className="admin-modal-select admin-modal-select-with-arrow"
            >
              <option value="ok">OK</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
              <option value="payment">Payment</option>
              <option value="topup">Topup</option>
              <option value="mail">Mail</option>
              <option value="settings">Settings</option>
              <option value="advertisement">Advertisement</option>
              <option value="statistic">Statistic</option>
            </select>
          </div>
        </label>

        {/* Target type selector */}
        <label>
          Target *
          <div className="admin-modal-select-wrapper">
            <select
              name="targetType"
              value={formData.targetType}
              onChange={handleChange}
              disabled={saving}
              className="admin-modal-select admin-modal-select-with-arrow"
            >
              <option value="all">All Users</option>
              <option value="user">Specific User</option>
              <option value="role">Specific Role</option>
            </select>
          </div>
        </label>

        {/* Conditional target ID field */}
        {formData.targetType !== "all" && (
          <label>
            {formData.targetType === "user" ? "User ID/Email *" : "Role ID *"}
            <MainInput
              type="text"
              name="targetId"
              value={formData.targetId}
              onChange={handleChange}
              required
              disabled={saving}
              placeholder={formData.targetType === "user" ? "Enter user ID or email" : "Enter role ID (1-6)"}
            />
          </label>
        )}

        {/* Error message display */}
        {error && (
          <div className="admin-modal-error-wrapper">
            <ErrorMessage message={error} onClose={() => setError("")} />
          </div>
        )}

        {/* Success message display */}
        {success && (
          <div className="admin-modal-success-wrapper">
            <SuccessMessage message={success} onClose={() => setSuccess("")} />
          </div>
        )}
      </form>
    </Modal>
  );
};

export default AdminCreateNotificationModal;
